<template>
  <section class="z-0 mt-5">
    <el-form
      label-position="top"
      :model="enteredAmountForm"
      ref="enteredAmountForm"
      label-width="80px"
      :rules="rules"
    >
      <div class="relative">
        <p class="mb-3">Send</p>
        <div
          class="flex h-20 justify-between border p-0 rounded-md items-center"
        >
          <el-form-item class="mt-3" prop="enteredAmount">
            <el-input
              v-model="enteredAmountForm.enteredAmount"
              @input="handleInput('enteredAmount', calcFirstInput)"
              placeholder="0.00"
              class="text-black font-bold text-lg"
            >
              <p slot="prefix" class="mt-2.5 ml-1 text-black font-normal">
                {{ sendCurrencySymbol }}
              </p>
            </el-input>
          </el-form-item>
          <div
            class="flex md:w-24 w-28 items-center gap-x-1.5 mr-3 cursor-pointer"
            @click="showAccountSelection('showSourceAccountSelection')"
          >
            <div class="flex items-center gap-x-2.5">
              <currency-icons
                :currency="getCurrencyCountry(country.currency)"
                size="h-5 w-5"
              />
              <div class="capitalize text-sm">
                {{ country.currency }}
              </div>
            </div>
            <div>
              <caret-bottom-icon />
            </div>
          </div>
        </div>
        <transition name="fade">
          <select-account
            currencyType="source"
            class="absolute w-full -mt-2 right-0 left-0 z-30"
            v-if="showSourceAccountSelection"
            @closeAccountSelection="
              closeAccountSelection('showSourceAccountSelection')
            "
            @clearInput="clearInput"
          />
        </transition>
        <p class="text-primary font-semibold text-sm">
          Balance:
          {{ parseMoneyAll(this.selectedAccountBalance, country.currency) }}
        </p>
      </div>
      <div class="flex justify-center">
        <switch-icon @click.native="switchCurrency" class="cursor-pointer" />
      </div>
      <div class="relative">
        <p class="mb-3">You Get</p>
        <div
          class="flex justify-between h-20 border p-0 rounded-md items-center"
        >
          <el-form-item class="mt-3" prop="recipientAmount">
            <el-input
              v-model="enteredAmountForm.recipientAmount"
              placeholder="0.00"
              class="text-black font-bold text-lg"
              @input="handleInput('recipientAmount', calcSecondInput)"
            >
              <p slot="prefix" class="mt-2.5 ml-1 text-black font-normal">
                {{ receiveCurrencySymbol }}
              </p>
            </el-input>
          </el-form-item>
          <div
            @click="showAccountSelection('showDestinationAccountSelection')"
            class="flex md:w-24 w-28 items-center gap-x-1.5 mr-3 cursor-pointer"
          >
            <div class="flex items-center gap-x-2.5">
              <currency-icons
                :currency="getCurrencyCountry(country2.currency)"
                size="h-5 w-5"
              />
              <div class="capitalize text-sm">
                {{ country2.currency }}
              </div>
            </div>
            <div>
              <caret-bottom-icon />
            </div>
          </div>
        </div>
        <transition name="fade">
          <select-account
            currencyType="destination"
            class="absolute w-full right-0 left-0 z-30"
            v-if="showDestinationAccountSelection"
            @closeAccountSelection="
              closeAccountSelection('showDestinationAccountSelection')
            "
            @clearInput="clearInput"
          />
        </transition>
      </div>
      <div class="border-t border-b border-dashed mt-5">
        <div
          style="word-break: normal"
          class="border-b flex items-center justify-between p-4"
        >
          <p class="text-black text-sm font-normal">Exchange Rate</p>
          <div class="bg-info-subtle text-primary p-1.5 rounded-xl">
            1
            {{
              !this.isDivisionOperator ? country.currency : country2.currency
            }}
            = {{ exchangeRateBase }}
            {{
              !this.isDivisionOperator ? country2.currency : country.currency
            }}
          </div>
        </div>
        <div class="border-b flex items-center justify-between p-4">
          <p class="text-black text-sm font-normal">Our Fees</p>
          <p class="text-black text-sm font-semibold">0</p>
        </div>
        <div
          style="word-break: normal"
          class="flex items-center justify-between p-4"
        >
          <p class="text-black text-sm font-normal">
            Total Amount We'll Convert
          </p>
          <p class="text-black text-sm font-semibold">
            {{ addComma(formatAmount(enteredAmountForm.enteredAmount)) }}
            {{ country.currency }}
          </p>
        </div>
      </div>
      <el-button
        class="bg-primary w-full py-3.5 mt-4 text-white rounded-md text-sm font-normal"
        @click="validateForm('enteredAmountForm')"
        :loading="isLoading"
        >Continue</el-button
      >
    </el-form>
    <div
      v-if="showDestinationAccountSelection || showSourceAccountSelection"
      class="fixed left-0 right-0 top-0 bottom-0 h-screen bg-transparent"
      @click="closeDropdowns"
    ></div>
  </section>
</template>
<script>
import {
  GetCurrencyCountry,
  ParseMoneyAll,
  FormatAmount,
  AddComma,
  ParseAmount
} from '@/utils/helpers.js'
import { LOCAL_COUNTRIES, FIAT } from '@/utils/constants'
import totalAccounts from '@/mixins/getBalance.js'
import exchangeRateBase from '@/mixins/localDeposit.js'
import swap from '@/mixins/swap.js'
import tenant from '@/mixins/tenant.js'
export default {
  mixins: [totalAccounts, exchangeRateBase, tenant, swap],
  data () {
    return {
      showDestinationAccountSelection: false,
      showSourceAccountSelection: false,
      enteredAmountForm: {
        enteredAmount: '',
        recipientAmount: ''
      },
      firstInputSelected: true,
      switchRecipientAmount: 0,
      isLoading: false,
      localCountries: LOCAL_COUNTRIES,
      fiatCurrencies: FIAT,
      rules: {
        enteredAmount: [
          { validator: this.validateAmount, trigger: ['blur', 'change'] }
        ]
      }
    }
  },
  components: {
    CaretBottomIcon: () => import('@/assets/v2/icons/CaretBottomIcon'),
    CurrencyIcons: () => import('@/assets/icons/CurrencyIcons.vue'),
    SwitchIcon: () => import('@/assets/v2/icons/SwitchIcon.vue'),
    SelectAccount: () => import('@/components/v2/deposit/SelectAccount.vue')
  },
  methods: {
    closeDropdowns () {
      this.showDestinationAccountSelection = false
      this.showSourceAccountSelection = false
    },
    clearInput () {
      this.enteredAmountForm.recipientAmount = ''
      this.enteredAmountForm.enteredAmount = ''
    },
    showAccountSelection (arg) {
      this[arg] = !this[arg]
    },
    closeAccountSelection (arg) {
      this[arg] = !this[arg]
    },
    addComma (val) {
      return AddComma(val.toString())
    },
    validateAmount (rule, value, callback) {
      if (FormatAmount(value) > this.selectedAccountBalance) {
        callback(new Error("That's more than your balance"))
      } else if (!FormatAmount(value) || FormatAmount(value) < 1) {
        callback(new Error('Provide an amount'))
      } else {
        callback()
      }
    },
    formatAmount (val) {
      if (val.length > 1) {
        return FormatAmount(val)
      } else {
        return 0
      }
    },
    validateForm (enteredAmountForm) {
      this.$refs[enteredAmountForm].validate((valid) => {
        if (valid) {
          this.proceedToSwap()
        } else {
          this.$notify.error({
            title: 'Incomplete',
            message: 'please complete details',
            position: 'top-right'
          })
        }
      })
    },
    async proceedToSwap () {
      const payload = {
        amount: FormatAmount(this.enteredAmountForm.enteredAmount),
        fromCurrency: this.country.currency,
        toCurrency: this.country2.currency,
        toAmount: FormatAmount(this.enteredAmountForm.recipientAmount)
      }
      this.$store.dispatch('transactions/setSwapDetails', payload)
      this.$emit('showSwapDetails')
    },
    getCurrencyCountry (val) {
      return GetCurrencyCountry(val)
    },
    parseMoneyAll (amount, currency) {
      return ParseMoneyAll(amount, currency)
    }
  },
  watch: {
    'enteredAmountForm.enteredAmount': function (newValue) {
      const result = ParseAmount(newValue)
      this.$nextTick(() => {
        this.enteredAmountForm.enteredAmount = result
      })
    },
    'enteredAmountForm.recipientAmount': function (newValue) {
      const result = ParseAmount(newValue)
      this.$nextTick(() => {
        this.enteredAmountForm.recipientAmount = result
      })
    },
    deep: true
  },
  mounted () {
    const details = this.depositSwapCurrencies[0]
    this.$store.dispatch('common/setSecondaryCurrency', details.to)
    this.$store.dispatch('common/setActiveCurrency', details.from)
  },
  computed: {
    recipientAmount: function () {
      const exchangedAmount =
        FormatAmount(this.enteredAmountForm.enteredAmount) * this.exchangeRate
      if (!exchangedAmount || exchangedAmount <= 0) {
        return 0
      }
      return AddComma(parseFloat(exchangedAmount).toFixed(2).toString())
    },
    enteredAmount: function () {
      const amountToConvert =
        FormatAmount(this.enteredAmountForm.recipientAmount) / this.exchangeRate
      return AddComma(parseFloat(amountToConvert).toFixed(2).toString())
    },
    isSwapUsdToLocal () {
      return !this.isSwapLocalToUsd
    },
    isSwapLocalToUsd () {
      const localCountry = LOCAL_COUNTRIES.find(
        (localCountry) => localCountry.currency === this.country.currency
      )
      return !!localCountry
    },
    selectedAccountBalance () {
      const selectedAccount = this.totalAccounts.find(
        (account) => account.currency === this.country.currency
      )
      return selectedAccount ? selectedAccount.currentBalance : 0
    },
    sendCurrencySymbol () {
      const details = this.fiatCurrencies.find(
        (country) => country.currency === this.country.currency
      )
      return details ? details.symbol : '$'
    },
    receiveCurrencySymbol () {
      const details = this.fiatCurrencies.find(
        (country) => country.currency === this.country2.currency
      )
      return details ? details.symbol : '$'
    }
  }
}
</script>
<style scoped>
>>> .el-input__inner {
  background: var(--input-background);
  color: var(--input-text);
  z-index: 0;
}
>>> .el-form-item__error {
  left: 15px;
  font-size: 10px;
}
>>> .el-input--prefix .el-input__inner {
  padding-left: 50px;
}
.border {
  border: 1px solid var(--border-default);
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}

.fade-enter, .fade-leave-to /* .fade-leave-active in <2.1.8 */ {
  opacity: 0;
}
</style>
